import React, { forwardRef } from 'react';

const Settings = forwardRef(({ updateSetting, handleColorTheme }, ref) => {
  return (
    <>
      <button 
        className="btn btn-primary p-3 rounded-circle d-flex align-items-center justify-content-center customizer-btn"
        type="button" 
        data-bs-toggle="offcanvas" 
        data-bs-target="#settings-offcanvas" 
        aria-controls="settings-offcanvas"
      >
        <i className="icon ti ti-settings fs-7"></i>
      </button>

      <div className="offcanvas customizer offcanvas-end" tabIndex="-1" id="settings-offcanvas"
        aria-labelledby="settings-offcanvas-label" ref={ref}>
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <h4 className="offcanvas-title fw-semibold" id="settings-offcanvas-label">
            Settings
          </h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div className="offcanvas-body" data-simplebar style={{ height: 'calc(100vh - 80px)' }}>
          <h6 className="fw-semibold fs-4 mb-2">Theme</h6>
          <div className="d-flex flex-row gap-3 customizer-box" role="group">
            <input 
              type="radio" 
              className="btn-check" 
              name="theme-layout" 
              id="light-layout" 
              autoComplete="off"
              onChange={() => updateSetting('theme', 'light')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="light-layout">
              <i className="icon ti ti-brightness-up fs-7 me-2"></i>Light
            </label>

            <input 
              type="radio" 
              className="btn-check" 
              name="theme-layout" 
              id="dark-layout" 
              autoComplete="off"
              onChange={() => updateSetting('theme', 'dark')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="dark-layout">
              <i className="icon ti ti-moon fs-7 me-2"></i>Dark
            </label>
          </div>

          <h6 className="mt-5 fw-semibold fs-4 mb-2">Theme Direction</h6>
          <div className="d-flex flex-row gap-3 customizer-box" role="group">
            <input 
              type="radio" 
              className="btn-check" 
              name="direction-l" 
              id="ltr-layout" 
              autoComplete="off"
              onChange={() => updateSetting('direction', 'ltr')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="ltr-layout">
              <i className="icon ti ti-text-direction-ltr fs-7 me-2"></i>LTR
            </label>

            <input 
              type="radio" 
              className="btn-check" 
              name="direction-l" 
              id="rtl-layout" 
              autoComplete="off"
              onChange={() => updateSetting('direction', 'rtl')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="rtl-layout">
              <i className="icon ti ti-text-direction-rtl fs-7 me-2"></i>RTL
            </label>
          </div>

          <h6 className="mt-5 fw-semibold fs-4 mb-2">Theme Colors</h6>
          <div className="d-flex flex-row flex-wrap gap-3 customizer-box color-pallete" role="group">
            {['Blue_Theme', 'Aqua_Theme', 'Purple_Theme', 'Green_Theme', 'Cyan_Theme', 'Orange_Theme'].map(theme => (
              <React.Fragment key={theme}>
                <input 
                  type="radio" 
                  className="btn-check" 
                  name="color-theme-layout" 
                  id={theme} 
                  autoComplete="off" 
                />
                <label 
                  className="btn p-9 btn-outline-primary d-flex align-items-center justify-content-center" 
                  onClick={() => handleColorTheme(theme)}
                  htmlFor={theme}
                  data-bs-toggle="tooltip" 
                  data-bs-placement="top" 
                  data-bs-title={theme.replace('_', ' ')}
                >
                  <div className={`color-box rounded-circle d-flex align-items-center justify-content-center skin-${['Blue', 'Aqua', 'Purple', 'Green', 'Cyan', 'Orange'].indexOf(theme.split('_')[0]) + 1}`}>
                    <i className="ti ti-check text-white d-flex icon fs-5"></i>
                  </div>
                </label>
              </React.Fragment>
            ))}
          </div>

          <h6 className="mt-5 fw-semibold fs-4 mb-2">Container Option</h6>
          <div className="d-flex flex-row gap-3 customizer-box" role="group">
            <input 
              type="radio" 
              className="btn-check" 
              name="layout" 
              id="boxed-layout" 
              autoComplete="off"
              onChange={() => updateSetting('container', 'boxed')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="boxed-layout">
              <i className="icon ti ti-layout-distribute-vertical fs-7 me-2"></i>Boxed
            </label>

            <input 
              type="radio" 
              className="btn-check" 
              name="layout" 
              id="full-layout" 
              autoComplete="off"
              onChange={() => updateSetting('container', 'full')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="full-layout">
              <i className="icon ti ti-layout-distribute-horizontal fs-7 me-2"></i>Full
            </label>
          </div>

          <h6 className="fw-semibold fs-4 mb-2 mt-5">Sidebar Type</h6>
          <div className="d-flex flex-row gap-3 customizer-box" role="group">
            <input 
              type="radio" 
              className="btn-check" 
              name="sidebar-type" 
              id="full-sidebar" 
              autoComplete="off"
              onChange={() => updateSetting('sidebarType', 'full')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="full-sidebar">
              <i className="icon ti ti-layout-sidebar-right fs-7 me-2"></i>Full
            </label>

            <input 
              type="radio" 
              className="btn-check" 
              name="sidebar-type" 
              id="mini-sidebar" 
              autoComplete="off"
              onChange={() => updateSetting('sidebarType', 'mini')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="mini-sidebar">
              <i className="icon ti ti-layout-sidebar fs-7 me-2"></i>Collapse
            </label>
          </div>

          <h6 className="mt-5 fw-semibold fs-4 mb-2">Card With</h6>
          <div className="d-flex flex-row gap-3 customizer-box" role="group">
            <input 
              type="radio" 
              className="btn-check" 
              name="card-layout" 
              id="card-with-border" 
              autoComplete="off"
              onChange={() => updateSetting('cardWith', 'border')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="card-with-border">
              <i className="icon ti ti-border-outer fs-7 me-2"></i>Border
            </label>

            <input 
              type="radio" 
              className="btn-check" 
              name="card-layout" 
              id="card-without-border" 
              autoComplete="off"
              onChange={() => updateSetting('cardWith', 'shadow')}
            />
            <label className="btn p-9 btn-outline-primary" htmlFor="card-without-border">
              <i className="icon ti ti-border-none fs-7 me-2"></i>Shadow
            </label>
          </div>
        </div>
      </div>
    </>
  );
});

Settings.displayName = 'Settings';

export default Settings;